import type { ReactNode } from "react";

import "./chip.css";

/**
 * A filter chip, and the labelled group of them.
 *
 * `ChipRow` is the group: an optional small-caps label over a wrapping row.
 * The label is what the reference design puts above each filter set — "USER",
 * "ACTION TYPE" — and it doubles as the group's accessible name, so a screen
 * reader hears which axis a chip belongs to before it hears the chip.
 *
 * A chip is `aria-pressed`, not a radio. It is a toggle over a list that is
 * already on screen, and the list is the answer — nothing is submitted.
 */
export function ChipRow({
  label,
  children,
}: {
  /** Shown above the row and used as its accessible name. */
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="chip-row" role="group" aria-label={label}>
      <span className="chip-row__label" aria-hidden="true">
        {label}
      </span>
      <div className="chips">{children}</div>
    </div>
  );
}

export function Chip({
  pressed,
  onClick,
  disabled = false,
  title,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  /**
   * An axis this deployment records nothing for.
   *
   * Rendered rather than dropped, which is the same choice
   * `capabilities.transferWork` makes: the server says no and the client shows
   * the refusal, instead of hiding a control and leaving the reader to wonder
   * whether the feature or their data is missing. Disabled properly, so it
   * leaves the tab order rather than being a button that swallows presses.
   */
  disabled?: boolean;
  /** Why it is disabled. Also its accessible description. */
  title?: string | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`chip${pressed ? " chip--on" : ""}`}
      aria-pressed={pressed}
      disabled={disabled}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
